import * as ko from 'knockout';

import i18n from '../i18n';
import { ListRequestParams } from '../api/request';
import { ListLoaderDelegate, delegateActions } from '../components/list_loader';
import {
  OrganizationData,
  breedersApi,
  buyersApi,
  producersApi,
  customersApi,
  partnersApi,
  organizationTypesApi,
} from '../api/organizations';
import { countriesApi } from '../api/countries';
import { Organization } from '../models/organization';
import { session } from '../session';
import { ListHeaderAction } from '../components/list_header';
import { addChangelogListAction, addChangelogAction } from './changelog';
import { Action } from '../components/basic_widgets';
import { BaseLoadingScreen } from './base_loading_screen';
import { translate } from '../i18n_text';
import { canViewChangelog, reactListTablesEnabled } from '../permissions';
import { GridView } from '../utils/grid_view';
import { GridChoices, listLookup } from '../utils/grid_choices';
import {
  OrganizationsListChoices,
  OrganizationsListPage,
  OrganizationsListProps,
  OrganizationsListQuery,
  OrganizationsListRow,
} from 'react-islands';

let template = require('raw-loader!../../templates/organizations.html').default;

const baseNameToChangelog: { [key: string]: string } = {
  producers: 'producer',
  buyers: 'buyer',
  breeders: 'breeder',
};

/** The organization lists served by the React table; the other three keep the Knockout list. */
const ISLAND_BASE_NAMES = ['partners', 'customers'];

function emptyChoices(): OrganizationsListChoices {
  return { organizationType: [], country: [] };
}

class OrganizationsScreen
  extends BaseLoadingScreen
  implements ListLoaderDelegate<OrganizationData, Organization>
{
  private baseName: string;
  private api: typeof breedersApi;
  title: string;
  newUrl: string;

  canCreate = session.isAtLeastEditor();

  listActions: ListHeaderAction[] = [];

  newOrganizationsTable: boolean;
  showUserLog = canViewChangelog(null);

  private view = new GridView(
    'organizations',
    new GridChoices<OrganizationsListChoices>(
      {
        organization_type: listLookup(organizationTypesApi.list, 'organizationType'),
        country: listLookup(countriesApi.list, 'country'),
      },
      emptyChoices
    )
  );

  tableProps = ko.pureComputed<OrganizationsListProps>(() => ({
    view: this.view,
    fetchPage: this.fetchPage,
    showUserLog: this.showUserLog,
  }));

  constructor(params: { baseName: string; api: typeof breedersApi; title: string; filters?: unknown }) {
    super();

    this.baseName = params.baseName;
    this.api = params.api;
    this.title = params.title;
    this.newUrl = '/' + this.baseName + '/new/';
    this.newOrganizationsTable = ISLAND_BASE_NAMES.indexOf(this.baseName) !== -1 && reactListTablesEnabled();

    if (this.baseName === 'producers') {
      this.listActions.push({
        title: i18n.t('Producers tasks')(),
        icon: 'assignment_ind',
        href: '/tasks/',
      });
    }
    if (this.baseName === 'buyers') {
      this.listActions.push({
        title: i18n.t('Buyers tasks')(),
        icon: 'assignment_ind',
        href: '/buyer_tasks/',
      });
    }

    if (this.baseName === 'breeders' || this.baseName === 'customers' || this.baseName === 'partners') {
      if (this.canCreate) {
        this.listActions.push({
          title: i18n.t('Import')(),
          icon: 'file_upload',
          href: `/${this.baseName}/import/`,
        });
      }
    }
    if (this.canCreate) {
      this.listActions.splice(0, 0, {
        title: i18n.t('Add')(),
        icon: 'add_circle',
        href: this.newUrl,
      });
    }
    this.listActions = addChangelogListAction(baseNameToChangelog[this.baseName], this.listActions);

    if (this.newOrganizationsTable) {
      this.loadedAfter(this.view.load(params.filters));
    } else {
      this.loaded();
    }
  }

  fetch(params: ListRequestParams) {
    return this.api.list(params);
  }

  private fetchPage = (query: OrganizationsListQuery): Promise<OrganizationsListPage> =>
    this.view.fetchListPage(query, this.api.list, 'name_prefix', this);

  toRow = (organization: Organization): OrganizationsListRow => {
    const organizationType = organization.organizationType();
    const country = organization.country();
    const data = organization.data;

    return {
      id: organization.id(),
      name: organization.name(),
      url: session.toTenantPath(organization.editUrl),
      organizationTypeName: organizationType ? translate(organizationType.name_json) : '',
      countryName: country ? country.name : '',
      publicTypeName: organization.publicTypeName(),
      createdByName: (data && data.created_by_user_name) || '',
      modifiedByName: (data && data.modified_by_user_name) || '',
      actions: delegateActions(this, organization, this.view.reload),
    };
  };

  instantiate(data: OrganizationData) {
    return new Organization(this.baseName, data);
  }

  getEditUrl(entity: Organization) {
    return entity.editUrl;
  }

  remove(id: string) {
    return this.api.remove(id);
  }

  canRemove(entity: Organization) {
    return entity.canEdit();
  }

  getActions(entity: Organization): Action[] {
    return addChangelogAction(baseNameToChangelog[this.baseName], entity.id());
  }
}

export let breeders = {
  name: 'breeders',
  viewModel: {
    createViewModel: (params: { filters?: unknown }) =>
      new OrganizationsScreen({
        baseName: 'breeders',
        api: breedersApi,
        title: i18n.t('Breeders')(),
        filters: params.filters,
      }),
  },
  template: template,
};
ko.components.register(breeders.name, breeders);

export let buyers = {
  name: 'buyers',
  viewModel: {
    createViewModel: (params: { filters?: unknown }) =>
      new OrganizationsScreen({
        baseName: 'buyers',
        api: buyersApi,
        title: i18n.t('Buyers')(),
        filters: params.filters,
      }),
  },
  template: template,
};
ko.components.register(buyers.name, buyers);

export let producers = {
  name: 'producers',
  viewModel: {
    createViewModel: (params: { filters?: unknown }) =>
      new OrganizationsScreen({
        baseName: 'producers',
        api: producersApi,
        title: i18n.t('Producers/Sellers')(),
        filters: params.filters,
      }),
  },
  template: template,
};
ko.components.register(producers.name, producers);

export let customers = {
  name: 'customers',
  viewModel: {
    createViewModel: (params: { filters?: unknown }) =>
      new OrganizationsScreen({
        baseName: 'customers',
        api: customersApi,
        title: i18n.t('Customers')(),
        filters: params.filters,
      }),
  },
  template: template,
};
ko.components.register(customers.name, customers);

export let partners = {
  name: 'partners',
  viewModel: {
    createViewModel: (params: { filters?: unknown }) =>
      new OrganizationsScreen({
        baseName: 'partners',
        api: partnersApi,
        title: i18n.t('Partners')(),
        filters: params.filters,
      }),
  },
  template: template,
};
ko.components.register(partners.name, partners);
